/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Button, Text, UnstyledButton } from '@/shared/ui';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { cn } from '@/shared/ui/utils/tailwind';

interface PropertyDialogProps {
  title: string;
  subtitle?: string;
  /** Desktop width. Ignored on mobile, where the sheet is full width. */
  width?: string;
  /** Confirm label. Omitted renders a dialog with no footer. */
  confirmLabel?: string;
  onConfirm?: () => void;
  confirmDisabled?: boolean;
  /** Paints the confirm button as destructive. */
  destructive?: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Explanatory line under the body, above the footer. */
  note?: string;
  'data-testid'?: string;
}

/**
 * The one dialog shell the property detail screen uses everywhere.
 *
 * Desktop centres a capped-width card; mobile turns the same dialog into a
 * bottom sheet, because a centred card on a phone leaves the confirm button
 * under the thumb's reach and the scrim above it doing nothing. On the sheet
 * the confirm sits full width *above* cancel: the destructive or committing
 * action is the one the thumb lands on first, so it gets the deliberate
 * position rather than the convenient one.
 *
 * @param props - Dialog content, actions and sizing.
 * @returns The rendered dialog.
 */
export const PropertyDialog: React.FC<PropertyDialogProps> = ({
  title,
  subtitle,
  width = '520px',
  confirmLabel,
  onConfirm,
  confirmDisabled = false,
  destructive = false,
  onClose,
  children,
  note,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div
      className={cn(
        'fixed inset-0 z-[1200] flex justify-center bg-[var(--overlay-scrim)]',
        isMobile ? 'items-end' : 'items-center p-[var(--space-5)]',
      )}
      onClick={onClose}
      data-testid={dataTestId}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        style={isMobile ? undefined : { maxWidth: width }}
        className={cn(
          'flex w-full flex-col overflow-hidden bg-[var(--surface-raised)] shadow-[var(--shadow-lg)]',
          isMobile
            ? 'max-h-[90vh] rounded-t-(--radius-xl)'
            : 'max-h-[88vh] rounded-(--radius-xl)',
        )}
      >
        <div className="flex flex-none items-start gap-[var(--space-3)] border-b border-[var(--border-subtle)] px-[var(--space-5)] py-[var(--space-4)]">
          <div className="flex min-w-0 flex-1 flex-col gap-[var(--space-0-5)]">
            <Text
              component="span"
              className="text-[length:var(--text-app-section)] [font-weight:var(--font-bold)] leading-[var(--leading-snug)] text-[var(--text-primary)]"
            >
              {title}
            </Text>
            {subtitle && (
              <Text
                component="span"
                className="text-[length:var(--text-meta)] leading-[var(--leading-normal)] text-[var(--text-tertiary-soft)]"
              >
                {subtitle}
              </Text>
            )}
          </div>
          <UnstyledButton
            type="button"
            onClick={onClose}
            aria-label={t('buttons.close')}
            className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-(--radius-md) text-[var(--text-tertiary-soft)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]"
          >
            <Cross2Icon width={16} height={16} />
          </UnstyledButton>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-[var(--space-5)]">
          {children}
          {note && (
            <Text className="mt-[var(--space-4)] m-0 text-[length:var(--text-meta)] leading-[var(--leading-normal)] text-[var(--text-tertiary-soft)]">
              {note}
            </Text>
          )}
        </div>

        {confirmLabel && onConfirm && (
          <div
            className={cn(
              'flex flex-none gap-[var(--space-2)] border-t border-[var(--border-subtle)] px-[var(--space-5)] py-[var(--space-4)]',
              isMobile ? 'flex-col-reverse' : 'justify-end',
            )}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={onClose}
              className={cn('whitespace-nowrap', isMobile && 'h-[48px] w-full')}
            >
              {t('buttons.cancel')}
            </Button>
            <Button
              type="button"
              variant="solid"
              onClick={onConfirm}
              disabled={confirmDisabled}
              style={
                destructive
                  ? { background: 'var(--status-error-text)', color: 'var(--text-inverse)' }
                  : undefined
              }
              className={cn('whitespace-nowrap', isMobile && 'h-[48px] w-full')}
            >
              {confirmLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
